import { createAsyncThunk } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import API from "../../Common/Endpoint/serviceConstrants";
import axiosInstanceClient from '../../Common/Axios/axiosInstanceClient';

const initialState = {
  isLoading: false,
  allofferData: [],
  error: null,
}

const OFFER_REQUEST_REUSE_MS = 3000;
const recentOfferRequests = new Map();


export const GetOfferData = createAsyncThunk('GetOfferData', async (payload) => {
  const {
    pageNumber,
    pageSize,
    sectionId,
    filters,
    extraParams = {},
    extraHeaders = {},
    memberIdentifier,
    cardsNetwork,
    cardCodeValue,
    search = '',
    sourceType,
    sourceValue,
  } = payload;

  let finalFilter
  // Improved filter mapping: only include non-empty filter arrays
  const filterKeys = ['brand', 'category', 'validity', 'offer'];
  finalFilter = {};
  filterKeys.forEach(key => {
    if (Array.isArray(filters?.[key]) && filters[key].length > 0) {
      // Capitalize key for API contract
      const apiKey = key.charAt(0).toUpperCase() + key.slice(1);
      finalFilter[apiKey] = filters[key].map(item => item);
    }
  });

  // If no filters are present, set finalFilter to undefined
  if (Object.keys(finalFilter).length === 0) {
    finalFilter = undefined;
  }


  let params;
  if (finalFilter) {
    params = {
      pageNumber,
      pageSize,
      Search: search,
      ...finalFilter,
      ...(sectionId && { sectionId }),
      ...extraParams,
    };
  } else {
    params = {
      pageNumber,
      pageSize,
      Search: search,
      ...(sectionId && { sectionId }),
      ...extraParams
    };
  }
  if (sourceType) {
    params.sourceType = sourceType;
  }
  if (sourceValue) {
    params.sourceValue = sourceValue;
  }

  // Only add cardsNetwork param if it is defined, is an array, and has values
  if (memberIdentifier && Array.isArray(cardsNetwork) && cardsNetwork.length > 0) {
    params.cardsNetwork = encodeURIComponent(cardsNetwork.join(','));
    params.cardCode = encodeURIComponent(cardCodeValue.join(','));
  } else {
    // Ensure cardsNetwork is not sent if not available
    if (params.cardsNetwork !== undefined) {
      delete params.cardsNetwork;
    }
  }

  // Convert array values in params to comma-separated strings
  Object.keys(params).forEach(key => {
    if (Array.isArray(params[key])) {
      params[key] = params[key].join(',');
    }
  });


  let headers = {
    "Customer-Identifier": memberIdentifier,
    ...extraHeaders
  };

  // Several callers can ask for the exact same deals page within moments of each other — e.g. the
  // home page's FeaturedOffer is remounted repeatedly while the page settles, and each mount
  // fires its own fallback fetch. Share one request between identical calls that are either still
  // in flight or finished within the last OFFER_REQUEST_REUSE_MS, instead of hitting the API again.
  const requestKey = JSON.stringify([!!memberIdentifier, params, headers]);
  const recent = recentOfferRequests.get(requestKey);
  if (recent && (recent.settledAt === null || Date.now() - recent.settledAt < OFFER_REQUEST_REUSE_MS)) {
    return recent.promise;
  }

  recentOfferRequests.forEach((value, key) => {
    if (value.settledAt !== null && Date.now() - value.settledAt >= OFFER_REQUEST_REUSE_MS) recentOfferRequests.delete(key);
  });
  const entry = { promise: null, settledAt: null };
  entry.promise = (async () => {
    try {
      const response = memberIdentifier ? await axiosInstanceClient.get(API.dealsPostLogin, { params, headers }) : await axiosInstanceClient.get(API.dealsPreLogin, { params });
      entry.settledAt = Date.now();
      return response;
    } catch (error) {
      recentOfferRequests.delete(requestKey);
      return error.response;
    }
  })();
  recentOfferRequests.set(requestKey, entry);
  return entry.promise;
})


export const dealsDataSlice = createSlice({
  name: "deals-post-login",
  initialState,
  reducers: {
    onGetDealsPreLogin: (state) => {
      return {
        ...state,
        isLoading: true,
        data: [],
        message: "",
        status_code: undefined,
      };
    },

    onGetDealsPreLoginSuccess: (state, { payload }) => {
      const { data = [], message = "", status_code = 200 } = payload || {};

      const newOfferPageData = { ...data.items[0], totalRecord: data?.totalRecords };

      const persistedData = JSON.parse(sessionStorage.getItem('persist:root'));
      const presistOldOfferData = JSON.parse(persistedData?.dealsDataSlice) || [];
      const oldOfferData = presistOldOfferData?.offerData;

      let updatedOfferPageData

      if (data?.pageNumber >= 1) {
        updatedOfferPageData = {
          dealCategories: oldOfferData?.dealCategories.concat(data.items[0]?.dealCategories || []),
          dealCoupounFrequencies: oldOfferData?.dealCoupounFrequencies.concat(data.items[0]?.dealCoupounFrequencies || []),
          dealCoupouns: oldOfferData?.dealCoupouns.concat(data.items[0]?.dealCoupouns || []),
          deals: oldOfferData?.deals.concat(data.items[0]?.deals || []),
          filteredDealCategories: oldOfferData?.filteredDealCategories,
          filteredDeals: oldOfferData?.filteredDeals,
          sectionMasters: oldOfferData?.sectionMasters,
          sectionContentMasters: oldOfferData?.sectionContentMasters,
          filteredDays: oldOfferData?.filteredDays,
          filteredByOfferSubType: oldOfferData?.filteredByOfferSubType,
          totalRecord: data?.totalRecords
        }
      }
      return {
        ...state,
        isLoading: false,
        offerData: data?.pageNumber === 1 ? newOfferPageData : updatedOfferPageData,
        message,
        status_code,
      };
    },

    onGetDealsPreLoginError: (state, { payload }) => {
      const { data = [], message = "", status_code = 400 } = payload || {};
      return {
        ...state,
        isLoading: false,
        data,
        message,
        status_code,
      };
    },
    onGetDealsPostLogin: (state) => {
      return {
        ...state,
        isLoading: true,
        message: "",
        status_code: undefined,
      };
    },

    onGetDealsPostLoginSuccess: (state, { payload }) => {
      const { data = [], message = "", status_code = 200 } = payload || {};

      const newOfferPageData = [data.items[0], data?.totalRecords];

      const persistedData = JSON.parse(sessionStorage.getItem('persist:root'));
      const presistOldOfferData = JSON.parse(persistedData?.dealsDataSlice) || [];
      const oldOfferData = presistOldOfferData?.offerData;

      let updatedOfferPageData

      if (data?.pageNumber >= 1) {
        updatedOfferPageData = {
          dealCategories: oldOfferData?.dealCategories.concat(data.items[0]?.dealCategories || []),
          dealCoupounFrequencies: oldOfferData?.dealCoupounFrequencies.concat(data.items[0]?.dealCoupounFrequencies || []),
          dealCoupouns: oldOfferData?.dealCoupouns.concat(data.items[0]?.dealCoupouns || []),
          deals: oldOfferData?.deals.concat(data.items[0]?.deals || []),
          filteredDealCategories: oldOfferData?.filteredDealCategories,
          filteredDeals: oldOfferData?.filteredDeals,
          sectionMasters: oldOfferData?.sectionMasters,
          sectionContentMasters: oldOfferData?.sectionContentMasters,
          filteredDays: oldOfferData?.filteredDays,
          filteredByOfferSubType: oldOfferData?.filteredByOfferSubType,
          totalRecord: data?.totalRecords
        }
      }
      return {
        ...state,
        isLoading: false,
        data: data,
        offerData: data?.pageNumber === 1 ? newOfferPageData : updatedOfferPageData,
        message,
        status_code,
      };
    },

    onGetDealsPostLoginError: (state, { payload }) => {
      const { data = [], message = "", status_code = 400 } = payload || {};
      return {
        ...state,
        isLoading: false,
        data,
        message,
        status_code,
      };
    },
    onGetDealsReset: (state) => {
      return {
        ...state,
        message: "",
        offerData: null,
        data: null,
        status_code: null,
        isLoading: false,
        isError: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetOfferData.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(GetOfferData.fulfilled, (state, action) => {
        let finalData = {
          ...action.payload?.data?.response?.items[0],
          dealCoupounFrequencies: action?.payload?.data?.response?.pageNumber === 1 ? action.payload?.data?.response?.items[0]?.dealCoupounFrequencies : state?.allofferData?.dealCoupounFrequencies?.concat(action.payload?.data?.response?.items[0]?.dealCoupounFrequencies || []),
          deals: action.payload?.data?.response?.pageNumber === 1 ? action?.payload?.data?.response?.items[0]?.deals : state?.allofferData?.deals?.concat(action.payload?.data?.response?.items[0]?.deals || []),
        };
        return {
          ...state,
          isLoading: false,
          allofferData: finalData,
          error: null,
        }
      })
      .addCase(GetOfferData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.message;
      });
  }
});

export const {
  onGetDealsPostLogin,
  onGetDealsPostLoginSuccess,
  onGetDealsPostLoginError,
  onGetDealsPreLogin,
  onGetDealsPreLoginSuccess,
  onGetDealsPreLoginError,
  onGetDealsReset
} = dealsDataSlice.actions;

export default dealsDataSlice.reducer;